import React, { useEffect, useRef, useState } from "react";
import {
  createChart,
  CrosshairMode,
  CandlestickSeries,
  createSeriesMarkers,
} from "lightweight-charts";

/**
 * ---------- Helpers ----------
 */
function mapCandles(candles) {
  return (candles || []).map((x) => ({
    time: x.t, // epoch seconds
    open: x.o,
    high: x.h,
    low: x.l,
    close: x.c,
  }));
}

function mapMarkers(events) {
  return (events || []).map((e) => {
    const isUp = e.type === "BREAKOUT_UP";
    const isDown = e.type === "BREAKOUT_DOWN";
    const isRetest = e.type === "RETEST";

    const position = isUp ? "belowBar" : isDown ? "aboveBar" : "inBar";
    const shape = isRetest ? "circle" : isUp ? "arrowUp" : isDown ? "arrowDown" : "square";
    const text =
      e.type === "BREAKOUT_UP"
        ? "BO↑"
        : e.type === "BREAKOUT_DOWN"
        ? "BO↓"
        : e.type === "RETEST"
        ? "RT"
        : "FX";

    return {
      time: e.t,
      position,
      shape,
      text,
    };
  });
}

/**
 * ---------- Range Rectangle Primitive ----------
 * Draws shaded rectangles (tStart->tEnd, low->high) on candle pane.
 * Requires series.attachPrimitive (available in newer lightweight-charts).
 */
class RangeRectanglesPrimitive {
  constructor(series, ranges) {
    this._series = series;
    this._ranges = ranges || [];
  }

  updateRanges(ranges) {
    this._ranges = ranges || [];
  }

  paneViews() {
    return [
      {
        renderer: () => ({
          draw: (target) => {
            target.useBitmapCoordinateSpace((scope) => {
              const ctx = scope.context;
              const hpr = scope.horizontalPixelRatio;
              const vpr = scope.verticalPixelRatio;

              const chart = this._series.chart && this._series.chart();
              const ts = chart && chart.timeScale && chart.timeScale();
              if (!chart || !ts) return;

              for (const r of this._ranges) {
                const x1 = ts.timeToCoordinate(r.tStart);
                const x2 = ts.timeToCoordinate(r.tEnd);
                const y1 = this._series.priceToCoordinate(r.high);
                const y2 = this._series.priceToCoordinate(r.low);

                if (x1 == null || x2 == null || y1 == null || y2 == null) continue;

                const left = Math.min(x1, x2);
                const right = Math.max(x1, x2);
                const top = Math.min(y1, y2);
                const bottom = Math.max(y1, y2);

                const bx = left * hpr;
                const by = top * vpr;
                const bw = (right - left) * hpr;
                const bh = (bottom - top) * vpr;

                ctx.save();

                // Subtle fill
                const alpha =
                  r.type === "VALID_RANGE" ? 0.12 : r.type === "MINI_RANGE" ? 0.08 : 0.06;

                const fill =
                  r.status === "BROKEN"
                    ? `rgba(255, 0, 0, ${alpha})`
                    : r.type === "VALID_RANGE"
                    ? `rgba(0, 150, 255, ${alpha})`
                    : r.type === "MINI_RANGE"
                    ? `rgba(0, 200, 120, ${alpha})`
                    : `rgba(180, 180, 180, ${alpha})`;

                ctx.fillStyle = fill;
                ctx.fillRect(bx, by, bw, bh);

                // Border
                ctx.lineWidth = 1 * Math.max(hpr, vpr);
                ctx.strokeStyle =
                  r.status === "BROKEN"
                    ? "rgba(255,0,0,0.35)"
                    : "rgba(0,0,0,0.18)";
                ctx.strokeRect(bx, by, bw, bh);

                // Label
                if (r.label) {
                  ctx.font = `${12 * vpr}px Arial`;
                  ctx.fillStyle = "rgba(0,0,0,0.55)";
                  ctx.fillText(r.label, bx + 4 * hpr, by + 14 * vpr);
                }

                ctx.restore();
              }
            });
          },
        }),
      },
    ];
  }
}

function attachRangePrimitive(series, ranges) {
  const primitive = new RangeRectanglesPrimitive(series, ranges);
  if (typeof series.attachPrimitive === "function") {
    series.attachPrimitive(primitive);
  } else {
    console.warn(
      "attachPrimitive not available in this lightweight-charts version. Candles/SR/markers will work; ranges won't. Consider upgrading lightweight-charts."
    );
  }
  return primitive;
}

/**
 * ---------- Main Component ----------
 */
export default function SignalFlyChart({ symbol, timeFrame }) {
  const containerRef = useRef(null);
  const chartRef = useRef(null);
  const candleSeriesRef = useRef(null);
  const primitiveRef = useRef(null);

  const [data, setData] = useState(null);
  const markersApiRef = useRef(null);

  // 1) Fetch chart data (demo stub)
  useEffect(() => {
    let cancelled = false;

    async function load() {
      // Replace this with your backend call later.
      const json = {
        symbol,
        timeFrame,
        candles: [
          { t: 1700000000, o: 67810.5, h: 67840.1, l: 67790.0, c: 67820.3 },
          { t: 1700000300, o: 67820.3, h: 67860.0, l: 67810.2, c: 67855.8 },
          { t: 1700000600, o: 67855.8, h: 67870.0, l: 67800.0, c: 67810.0 },
          { t: 1700000900, o: 67810.0, h: 67890.0, l: 67805.0, c: 67888.0 },
          { t: 1700001200, o: 67888.0, h: 67920.0, l: 67870.0, c: 67910.0 },
        ],
        levels: [
          { id: "SR1", type: "SR", price: 67850.0, label: "Range High", strength: 4, active: true },
          { id: "SR2", type: "SR", price: 67500.0, label: "Range Low", strength: 3, active: true },
        ],
        ranges: [
          {
            id: "R1",
            type: "VALID_RANGE",
            tStart: 1700000000,
            tEnd: 1700001400,
            low: 67500.0,
            high: 67850.0,
            status: "ACTIVE",
            label: "Valid Range",
          },
        ],
        events: [
          { id: "E1", type: "BREAKOUT_UP", t: 1700000900, price: 67888.0, rangeId: "R1" },
          { id: "E2", type: "RETEST", t: 1700001200, price: 67850.0, rangeId: "R1" },
        ],
      };

      if (!cancelled) setData(json);
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [symbol, timeFrame]);

  // 2) Create chart once
  useEffect(() => {
    if (!containerRef.current) return;
    if (chartRef.current) return;

    const chart = createChart(containerRef.current, {
      autoSize: true,
      crosshair: { mode: CrosshairMode.Normal },
      rightPriceScale: { borderVisible: false },
      timeScale: { borderVisible: false, timeVisible: true, secondsVisible: false },
      layout: { background: { color: "white" }, textColor: "black" },
      grid: { vertLines: { visible: true }, horzLines: { visible: true } },
    });

    const candleSeries = chart.addSeries(CandlestickSeries, {});

    chartRef.current = chart;
    candleSeriesRef.current = candleSeries;

    const ro = new ResizeObserver(() => {
      chart.applyOptions({ autoSize: true });
    });
    ro.observe(containerRef.current);

    return () => {
      ro.disconnect();
      chart.remove();
      chartRef.current = null;
      candleSeriesRef.current = null;
      primitiveRef.current = null;
    };
  }, []);

  // 3) Bind data
  useEffect(() => {
    if (!data) return;
    const chart = chartRef.current;
    const series = candleSeriesRef.current;
    if (!chart || !series) return;

    const candleData = mapCandles(data.candles);
    series.setData(candleData);

    // SR lines: clear old then recreate
    if (series._sfPriceLines) {
      for (const pl of series._sfPriceLines) {
        try {
          series.removePriceLine(pl);
        } catch {}
      }
    }
    series._sfPriceLines = [];

    for (const lvl of data.levels || []) {
      if (lvl.active === false) continue;
      const pl = series.createPriceLine({
        price: lvl.price,
        title: lvl.label || "SR",
        lineWidth: Math.min(4, Math.max(1, lvl.strength || 2)),
      });
      series._sfPriceLines.push(pl);
    }

    // Markers
const markers = mapMarkers(data.events);

// Create once, then update
if (!markersApiRef.current) {
  markersApiRef.current = createSeriesMarkers(series, markers);
} else {
  markersApiRef.current.setMarkers(markers);
}

    // Ranges via primitive
    if (!primitiveRef.current) {
      primitiveRef.current = attachRangePrimitive(series, data.ranges);
    } else {
      primitiveRef.current.updateRanges(data.ranges);
      // Trigger redraw
      series.setData(candleData);
    }

    chart.timeScale().fitContent();
  }, [data]);

  return (
    <div
      style={{
        width: "100%",
        height: "520px",
        border: "1px solid rgba(0,0,0,0.08)",
        borderRadius: 10,
      }}
    >
      <div
        style={{
          padding: "10px 12px",
          fontSize: 14,
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <b>{symbol}</b> • TF {timeFrame}m • SignalFly Structure View
      </div>

      <div ref={containerRef} style={{ width: "100%", height: "470px" }} />
    </div>
  );
}